/**
 * The guided walkthrough's overlay.
 *
 * ─── The layer must not eat the clicks it is pointing at ────────────────────
 * Step 4 of the tour highlights the Inventory button and says "that is where
 * you keep your stuff". A full-screen scrim above the HUD would cover the very
 * button it is drawing a ring around — the same defect the 2026-09-18
 * one-window-at-a-time work went after, in new clothes. So:
 *
 *   .bv-tut         pointer-events: none   the whole layer, always
 *   .bv-tut-card    pointer-events: auto   the card, so its buttons work
 *   .bv-tut-ring    pointer-events: none   decoration over a live button
 *
 * There is no scrim at all while stepping. The dim is drawn by four huge
 * box-shadow spreads on the ring instead, which darkens everything EXCEPT the
 * highlighted box and still lets every click through.
 *
 * z-index 1150: above the HUD (1000-1002) and the game panels (1001-1100) so
 * the card is readable over whatever is open, below the tooltip layer (1200)
 * and Settings (1250) — the tour is started FROM Settings, which closes
 * itself first, and a tour that painted over an open Settings screen would be
 * unreachable rather than helpful.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export is one
 * template literal and a stray backtick terminates it.
 */
export default `
.bv-tut {
  position: fixed; inset: 0; z-index: 1150;
  pointer-events: none;
  font: 13px/1.55 var(--font-technical); color: var(--bone);
}
.bv-tut[hidden] { display: none; }

/* ── The highlight ────────────────────────────────────────────────────────
   Positioned from JS off the anchor's bounding box. The 200vmax spread is
   what dims the rest of the screen; it is on this element rather than on a
   separate scrim so that there is exactly one thing to position and exactly
   one thing that can be wrong. */
.bv-tut-ring {
  position: absolute; pointer-events: none;
  border: 2px solid var(--brass-hot); border-radius: var(--radius-well);
  box-shadow:
    0 0 0 200vmax rgba(5,3,2,0.62),
    0 0 18px rgba(255,189,92,0.55),
    inset 0 0 12px rgba(255,189,92,0.3);
  transition: top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease;
  animation: bv-tut-pulse 1.7s ease-in-out infinite;
}
@keyframes bv-tut-pulse {
  0%, 100% { border-color: var(--brass-hot); }
  50%      { border-color: rgba(255,189,92,0.42); }
}
/* A step with nothing to point at (the movement and mouse-look steps) still
   wants the screen dimmed, so the ring becomes an off-screen zero-box that
   keeps only its spread. Hidden border, no pulse — otherwise a 2px brass line
   flickers in the corner. */
.bv-tut-ring-none {
  border-color: transparent; animation: none;
  box-shadow: 0 0 0 200vmax rgba(5,3,2,0.62);
}

/* ── The card ─────────────────────────────────────────────────────────────
   The only part of this layer that takes a pointer. */
.bv-tut-card {
  position: absolute; pointer-events: auto;
  width: min(392px, calc(100vw - 32px));
  padding: 17px 19px 15px;
  background:
    repeating-linear-gradient(112deg, rgba(0,0,0,0.12) 0 2px, rgba(0,0,0,0) 2px 13px),
    linear-gradient(158deg, #33281a, var(--panel-a) 52%, #17110b);
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  box-shadow: 0 20px 52px rgba(0,0,0,0.72), inset 0 1px 0 rgba(255,255,255,0.06);
  transition: top 0.2s ease, left 0.2s ease;
}
/* The opening prompt is centred rather than anchored — it points at nothing
   and is the widest text in the tour. */
.bv-tut-card-center {
  top: 50%; left: 50%; transform: translate(-50%, -50%);
  width: min(492px, calc(100vw - 32px));
  padding: 24px 26px 20px;
}

.bv-tut-kicker {
  margin: 0 0 5px; color: var(--brass);
  font: 9px var(--font-technical); letter-spacing: 0.22em; text-transform: uppercase;
}
.bv-tut-title {
  margin: 0 0 9px;
  font: 700 19px/1.15 var(--font-display); letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--brass-hot);
}
.bv-tut-body { margin: 0; color: var(--bone-dim); font: 13px/1.6 var(--font-body); }
.bv-tut-body + .bv-tut-body { margin-top: 9px; }
/* Key names inside the prose. A <kbd> rather than bold, because half of these
   steps are about which key does what. */
.bv-tut-card kbd {
  display: inline-block; padding: 1px 6px; margin: 0 1px;
  background: var(--well); border: 1px solid var(--line); border-radius: var(--radius-tape);
  color: var(--brass-bright); font: 11px var(--font-technical); letter-spacing: 0.06em;
}

/* ── Footer: progress on the left, buttons on the right ─────────────────── */
.bv-tut-foot {
  display: flex; align-items: center; gap: 9px;
  margin-top: 17px; padding-top: 13px; border-top: 1px solid var(--line);
}
.bv-tut-count {
  margin-right: auto; color: var(--bone-faint);
  font: 10px var(--font-technical); letter-spacing: 0.16em; text-transform: uppercase;
}
.bv-tut-btn {
  padding: 8px 15px; cursor: pointer;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: rgba(122,90,48,0.3); color: var(--bone);
  font: 700 11px var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.bv-tut-btn:hover { background: rgba(122,90,48,0.55); }
.bv-tut-btn-go {
  background: linear-gradient(160deg, var(--brass-hot), var(--brass));
  border-color: var(--brass); color: #1a1208;
}
.bv-tut-btn-go:hover { background: linear-gradient(160deg, #ffd08a, var(--brass-hot)); }
.bv-tut-btn-quiet { background: none; color: var(--bone-faint); border-color: transparent; }
.bv-tut-btn-quiet:hover { background: none; color: var(--bone-dim); }

@media (prefers-reduced-motion: reduce) {
  .bv-tut-ring { animation: none; transition: none; }
  .bv-tut-card { transition: none; }
}
`;
